import type { SocialMember } from './types';
import type { ClubTheme } from './themes';
import ClubBadge from './ClubBadge';
import LinkButton from './LinkButton';

interface MemberCardProps {
  member: SocialMember;
  clubId: string;
  currentUserId?: string;
  theme: ClubTheme;
  onMessage?: (userId: string) => void;
  onViewProfile?: (userId: string) => void;
}

export default function MemberCard({ member, clubId, currentUserId, theme, onMessage, onViewProfile }: MemberCardProps) {
  const name = member.user?.full_name || 'Unknown';
  const avatarUrl = member.user?.avatar_url;
  const joinedDate = new Date(member.joined_at).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });

  const handleNameClick = () => {
    if (onViewProfile && member.user?.id) {
      onViewProfile(member.user.id);
    }
  };

  return (
    <div
      className="flex items-center gap-3 rounded-xl p-3 transition-colors"
      style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.muted}20`,
      }}
    >
      {/* Avatar — clickable */}
      <button type="button" onClick={handleNameClick} className="shrink-0">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="h-10 w-10 rounded-full object-cover"
          />
        ) : (
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold"
            style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
          >
            {(name[0] || '?').toUpperCase()}
          </div>
        )}
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleNameClick}
            className="text-sm font-semibold truncate hover:underline"
            style={{ color: theme.colors.text }}
          >
            {name}
          </button>
          <ClubBadge role={member.club_role} theme={theme} />
        </div>
        <p className="text-[11px]" style={{ color: theme.colors.muted }}>
          Joined {joinedDate}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Link button — don't show for yourself */}
        {member.user?.id && member.user.id !== currentUserId && (
          <LinkButton
            clubId={clubId}
            targetId={member.user.id}
            theme={theme}
            showMenu
          />
        )}

        {/* Message button */}
        {onMessage && member.user?.id && (
          <button
            type="button"
            onClick={() => onMessage(member.user!.id)}
            className="shrink-0 rounded-lg px-2.5 py-1 text-[11px] font-medium transition-colors"
            style={{
              backgroundColor: `${theme.colors.primary}18`,
              color: theme.colors.primary,
            }}
          >
            Message
          </button>
        )}
      </div>
    </div>
  );
}
